import React from "react";
import { Table, Header, TableProps } from "@amzn/awsui-components-react";
import { FixedFeeConfig } from "@amzn/fuse-accounting-central-datastore-client";

interface Props {
  fixedFeeConfig: FixedFeeConfig;
}

const DUMMY_COLUMN_DEFINITION = {
  id: "",
  header: "",
  cell: () => "",
};

// Helper function to format numeric values
const formatNumber = (value: any) => {
  if (!value) return "-";
  return Number(value.string).toLocaleString("en-US", {
    maximumFractionDigits: 4,
  });
};

export const TopLevelTable: React.FC<Props> = ({ fixedFeeConfig }) => {
  const topLevelColumnDefinitions: TableProps.ColumnDefinition<any>[] = [
    DUMMY_COLUMN_DEFINITION,
    {
      id: "fixedFeeId",
      header: "Fixed Fee ID",
      cell: (item: any) => item.fixedFeeId || "-",
      sortingField: "fixedFeeId",
    },
    {
      id: "environment",
      header: "Environment",
      cell: (item: any) => item.environment || "-",
      sortingField: "environment",
    },
    {
      id: "dealStatus",
      header: "Deal Status",
      cell: (item: any) => item.dealStatus || "-",
      sortingField: "dealStatus",
    },
    {
      id: "resellerIds",
      header: "Reseller IDs",
      cell: (item: any) => item.dealConfig?.resellerIds?.join(", ") || "-",
      sortingField: "dealStatus",
      maxWidth: 200,
    },
    {
      id: "fusePlanIds",
      header: "Fuse Plan IDs",
      cell: (item: any) => item.dealConfig?.fusePlanIds?.join(", ") || "-",
      sortingField: "dealStatus",
      maxWidth: 300,
    },
    {
      id: "serviceProvider",
      header: "Service Provider",
      cell: (item: any) => item.serviceProvider || "-",
      sortingField: "serviceProvider",
    },
    {
      id: "dealStartDate",
      header: "Deal Start Date",
      cell: (item: any) => item.dealStartDate || "-",
      sortingField: "dealStartDate",
    },
    {
      id: "dealEndDate",
      header: "Deal End Date",
      cell: (item: any) => item.dealEndDate || "-",
      sortingField: "dealEndDate",
    },
    {
      id: "lastBenefitEndDate",
      header: "Last Benefit End Date",
      cell: (item: any) => item.lastBenefitEndDate || "-",
      sortingField: "lastBenefitEndDate",
    },
    {
      id: "contractPrice",
      header: "Contract Price",
      cell: (item: any) => formatNumber(item.contractPrice),
      sortingField: "contractPrice",
    },
    {
      id: "initialPlanPrice",
      header: "Initial Plan Price",
      cell: (item: any) => formatNumber(item.initialPlanPrice),
      sortingField: "initialPlanPrice",
    },
    {
      id: "currencyCode",
      header: "Currency Code",
      cell: (item: any) => item.contractCurrencyCode || "-",
      sortingField: "currencyCode",
    },
    {
      id: "adjustmentFrequency",
      header: "Adjustment Frequency",
      cell: (item: any) => item.adjustmentFrequency || "-",
      sortingField: "adjustmentFrequency",
    },
    {
      id: "invoiceFrequency",
      header: "Invoice Frequency",
      cell: (item: any) => item.invoiceFrequency || "-",
      sortingField: "invoiceFrequency",
    },
    {
      id: "marketplaceId",
      header: "Marketplace ID",
      cell: (item: any) => item.dealConfig?.marketplaceId || "-",
      sortingField: "marketplaceId",
    },
    {
      id: "accountingRegistrationId",
      header: "Accounting Registration ID",
      cell: (item: any) => item.accountingRegistrationId || "-",
      sortingField: "accountingRegistrationId",
    },
    DUMMY_COLUMN_DEFINITION,
  ];

  const topLevelData = [fixedFeeConfig];

  return (
    <Table
      columnDefinitions={topLevelColumnDefinitions}
      items={topLevelData}
      wrapLines={true}
      resizableColumns={false}
      header={<Header>Top-Level Information</Header>}
    />
  );
};
